import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { I18NextPipe } from 'angular-i18next';
import { interval, Subject, Subscription, forkJoin } from 'rxjs';
import { take, finalize } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { NGXLogger } from 'ngx-logger';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { queueOverviewConfig, queueDetailsItem } from 'src/app/app-models/realTimeModels';
import { IHelpDialogData } from 'src/app/models';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { MatDialog } from '@angular/material/dialog';
import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';
import { ChangeReservedAgentDialogComponent, ChangeReservedAgentDialogResult } from '../../real-time-dialogs/change-reserved-agent-dialog/change-reserved-agent-dialog.component';
import { PreviewRequestDialogComponent } from '../../real-time-dialogs/preview-request-dialog/preview-request-dialog.component';
import { SettingsService } from 'src/app/services/services/settings.service';

@Component({
    selector: 'app-real-time-overview-queue',
    templateUrl: './real-time-overview-queue.component.html',
    styleUrls: ['./real-time-overview-queue.component.scss'],
    standalone: false
})
export class RealTimeOverviewQueueComponent implements OnInit, OnDestroy {
  private readonly coreColumns: string[] = [
    'formattedNumberFrom',
    'callerName',
    'formattedType',
    'formattedSla',
    'formattedTimeInQueue',
    'formattedVip',
    'formattedCallback',
    'formattedReservedFlag',
    'systemQueue',
    'preview'
  ];

  displayedColumns: string[] = [];

  BreadcrumbsLink = [];

  dataSource = new MatTableDataSource<queueDetailsItem>([]);
  isLoading = true;
  filter = '';
  showClock = false;
  queueId!: number;

  showFeatureCallback = false;
  propertyAllowDelete = false;
  showSystemQueue = false;
  queueDescription?: string = '‒';

  selected: Record<string, boolean> = {};

  private readonly defaultDisplayRows = 100;
  public curDisplayRows = this.defaultDisplayRows;
  private curStartRow = 1;
  hasMore = false;

  private ajaxRefreshInterval = 10000;
  private pollingSub?: Subscription;
  private terminate$ = new Subject<void>();

  isSearching = false;
  searchKeyword = '';

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    private realTimeService: realTimeService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private i18nextPipe: I18NextPipe,
    private dialog: MatDialog,
    private logger: NGXLogger,
    private titleService: Title
  ) { }

  get anySelectable(): boolean {
    return (this.dataSource.data || []).filter(r => !!r.allowDelete).length > 0;
  }

  async ngOnInit(): Promise<void> {
    this.dataSource.sort = this.sort;

    const saved = localStorage.getItem('rt_showSystemQueue');
    this.showSystemQueue = saved === 'true';

    this.queueId = Number(this.route.snapshot.paramMap.get('id'));
    if (Number.isNaN(this.queueId)) {
      this.isLoading = false;
      this.dataSource.data = [];
      return;
    }

    // ✅ Fetch queue name ONCE for the header
    this.realTimeService.GetQueueName(this.queueId)
      .pipe(take(1))
      .subscribe(
        name => {
          this.queueDescription = (name || '').trim() || '‒';
          this.titleService.setTitle('Puzzel Admin - ' + this.queueDescription);
          this.BreadcrumbsLink = [
            { "label": "Home", "url": "/" },
            { "label": "Real-Time", "url": "/real-time/overview" },
            { "label": "Queue Overview", "url": "/real-time/overview" },
            { "label": name, "url": `/real-time/overview/queue/${this.queueId}` }];
        },
        _ => { this.queueDescription = '‒'; }
      );

    const cfg: any = await this.settingsService.realtimeSettings();

    this.showClock = false; //!!cfg?.showClock;
    this.showFeatureCallback = !!cfg?.showFeatureCallback;
    this.propertyAllowDelete = !!cfg?.propertyAllowDelete;

    this.displayedColumns = this.buildDisplayedColumns();

    const intervalMs = this.normalizeInterval(cfg?.ajaxRefreshInterval);
    this.ajaxRefreshInterval = intervalMs;

    this.refreshData();
    this.startPolling();

    this.dataSource.filterPredicate = (row, filterText) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.numberFrom,
        row.callerName,
        row.description,
        row.formattedType,
        row.mediaType as any,
        row.resolvedName,
        row.formattedReservedFlag,
        row.queueDescription
      ].some(v => (String(v || '')).toLowerCase().includes(f));
    };
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.terminate$.next();
    this.terminate$.complete();
  }

  private refreshData(showLoader = true): void {
    if ((this.anySelected || this.isSearching) && !showLoader) return;

    if (showLoader) this.isLoading = true;

    const rowsToLoad = Math.max(1, this.curDisplayRows || this.defaultDisplayRows);

    this.realTimeService
      .getQueueDetails(this.queueId, rowsToLoad, 1)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        rows => {
          // ⛔ Do NOT set queueDescription here anymore
          this.dataSource.data = rows ?? [];

          this.curStartRow = 1 + rowsToLoad;
          this.hasMore = (rows?.length || 0) === rowsToLoad;

          if (!this.anySelected) {
            this.selected = {};
          }
        },
        _err => {
          this.logger.error('Error fetching queue details:', _err);
          this.isLoading = false;
        }
      );
  }

  onLoadMore(): void {
    if (this.isLoading || this.isSearching) return;
    this.stopPolling();
    this.isLoading = true;

    const pageSize = this.defaultDisplayRows;
    const start = this.curStartRow;

    this.realTimeService
      .getQueueDetails(this.queueId, pageSize, start)
      .pipe(finalize(() => { this.isLoading = false; this.startPolling(); }))
      .subscribe(
        rows => {
          const current = this.dataSource.data || [];
          const next = rows ?? [];
          this.dataSource.data = current.concat(next);

          this.curStartRow += pageSize;
          this.curDisplayRows += pageSize;
          this.hasMore = next.length === pageSize;
        },
        _err => {
          this.logger.error('Error fetching queue details:', _err);
          this.isLoading = false;
        }
      );
  }

  onSearch(): void {
    const q = (this.searchKeyword || '').trim();
    if (!q) return;

    this.isSearching = true;
    this.stopPolling();
    this.isLoading = true;

    this.realTimeService
      .getQueueDetailsSearch(this.queueId, q)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        rows => {
          this.dataSource.data = rows ?? [];
          this.hasMore = false;
          this.curDisplayRows = this.dataSource.data.length;
          this.selected = {};
        },
        _err => {
          this.logger.error('Error fetching queue details:', _err);
          this.isLoading = false;
        }
      );
  }

  onClearSearch(): void {
    this.isSearching = false;
    this.searchKeyword = '';
    this.selected = {};

    this.curDisplayRows = this.defaultDisplayRows;
    this.curStartRow = 1;
    this.hasMore = false;

    this.refreshData(true);
    this.startPolling();
  }

  private buildDisplayedColumns(): string[] {
    const cols: string[] = [];
    if (this.propertyAllowDelete) cols.push('select');
    cols.push('formattedNumberFrom');
    cols.push('callerName');
    cols.push('formattedType');
    cols.push('formattedSla');
    cols.push('timeInQueue');
    cols.push('formattedVip');
    cols.push('formattedCallback');
    if (this.showFeatureCallback) cols.push('formattedCallbackScheduledTime');
    cols.push('formattedReservedFlag');
    cols.push('systemQueue');
    cols.push('preview');
    return cols;
  }

  private normalizeInterval(raw: unknown): number {
    let ms = Number(raw) || 10000;
    if (ms > 0 && ms <= 60) ms *= 1000;
    return Math.max(1000, ms);
  }

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.ajaxRefreshInterval).subscribe(() => {
      if (!this.anySelected && !this.isSearching) {
        this.refreshData(false);
      }
    });
  }
  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }
  private checkPollingState(): void {
    if (this.anySelected || this.isSearching) this.stopPolling();
    else this.startPolling();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filter = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }
  applyFilterFromProperty() {
    this.dataSource.filter = this.filter.trim().toLowerCase();
  }
  clearFilter() {
    this.filter = '';
    this.dataSource.filter = '';
  }

  openHelp(): void {
    const data: IHelpDialogData = {
      title: this.i18nextPipe.transform('realtime:help_title_queueDetails'),
      text: this.i18nextPipe.transform('realtime:help_queueDetails'),
      renderAsHtml: true
    };
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      data,
      width: '600px'
    });
  }

  get allSelectableIds(): string[] {
    return (this.dataSource.data || [])
      .filter((x: queueDetailsItem) => !!x.allowDelete)
      .map((x: queueDetailsItem) => x.requestId);
  }
  get allSelected(): boolean {
    const ids = this.allSelectableIds;
    return ids.length > 0 && ids.every(id => !!this.selected[id]);
  }
  get anySelected(): boolean {
    return Object.values(this.selected).some(v => v);
  }

  toggleSelectAll(checked: boolean) {
    const next = { ...this.selected };
    this.allSelectableIds.forEach(id => next[id] = checked);
    this.selected = next;
    this.checkPollingState();
  }
  toggleOne(id: string, checked: boolean) {
    this.selected = { ...this.selected, [id]: checked };
    this.checkPollingState();
  }

  isEmailMedia(mediaType: any): boolean {
    return mediaType === 'EMail' || mediaType === 7;
  }

  get canEditReserved(): boolean {
    if (!this.anySelected) return false;
    const byId = new Map(this.dataSource.data.map(r => [r.requestId, r]));
    const selectedIds = Object.keys(this.selected).filter(id => this.selected[id]);
    return selectedIds.every(id => this.isEmailMedia(byId.get(id)?.mediaType));
  }

  onPreview(row: queueDetailsItem, $event: Event) {
    $event.preventDefault();
    this.stopPolling();
    const dialogRef = this.dialog.open(PreviewRequestDialogComponent, {
      width: '720px',
      disableClose: false,
      data: { requestId: Number(row.requestId) }
    });
    dialogRef.afterClosed().subscribe(() => {
      if (!this.isSearching) this.startPolling();
    });
  }

  shortQueueDescription(row: queueDetailsItem): string {
    const full = row.queueDescription || '‒';
    return full.length > 25 ? `${full.slice(0, 25)}...` : full;
  }

  onToggleSystemQueue(checked: boolean) {
    this.showSystemQueue = checked;
    localStorage.setItem('rt_showSystemQueue', String(checked));
  }

  onDeleteSelected(): void {
    const ids = Object.keys(this.selected).filter(id => this.selected[id]);
    if (!ids.length) return;

    const numericIds = ids.map(x => Number(x)).filter(n => !Number.isNaN(n));

    const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      data: {
        title: this.i18nextPipe.transform('messages:Delete Confirmation'),
        showTitleInHeader: true,
        additionalText: this.i18nextPipe.transform('realtime:queuedetails_deleteNote'),
        confirmText: this.i18nextPipe.transform('Yes'),
        cancelText: this.i18nextPipe.transform('No'),
      }
    });

    dialogRef.afterClosed().subscribe(confirm => {
      if (!confirm) return;
      this.stopPolling();
      this.isLoading = true;

      this.realTimeService.RequestDelete(numericIds)
        .pipe(finalize(() => { this.isLoading = false; this.startPolling(); }))
        .subscribe(
          (res: any) => {
            if (res?.error) {
              const msgKey = String(res.message || 'shared_err_genericServerError');
              alert(this.i18nextPipe.transform(msgKey));
              return;
            }
            this.selected = {};
            this.refreshData(true);
          },
          _err => {
            this.logger.error('Error deleting request', _err);
            this.isLoading = false;
          }
        );
    });
  }

  onEditReserved(): void {
    const ids = Object.keys(this.selected).filter(id => this.selected[id]);
    if (!ids.length) return;

    const requestIds = ids.map(x => Number(x)).filter(n => !Number.isNaN(n));

    this.stopPolling();

    const dialogRef = this.dialog.open(ChangeReservedAgentDialogComponent, {
      width: '720px',
      disableClose: true,
      data: { requestIds, queueId: this.queueId }
    });

    dialogRef.afterClosed().subscribe((result: ChangeReservedAgentDialogResult | undefined) => {
      if (!this.isSearching) this.startPolling();

      if (!result || result.action === 'cancel') return;

      this.isLoading = true;

       const calls = ids.map(rid =>
        this.realTimeService.ReservedUserUpdate(
          (rid as any),
          (result as any).secondsFromNow,
          (result as any).agentId
        )
      );

      forkJoin(calls)
        .pipe(finalize(() => {
          this.isLoading = false;
          this.selected = {};
          this.startPolling();
        }))
        .subscribe(() => this.refreshData(true));

    });
  }

  trackByRequest = (_: number, r: queueDetailsItem) => r.requestId;
}
